import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { useStore } from './store';
import { t, formatMoney, type Lang } from './i18n';
import { VW, NODE_W, NODE_H, GAP, PAD_TOP } from './types';
import type { Entity, FlowNode, Allocation, EntityType, Side } from './types';

type PosKind = 'root' | 'cat' | 'name' | 'card' | 'payer';
interface Pos {
  id: string;
  kind: PosKind;
  x: number; // center x (px)
  y: number; // center y (px)
  w: number;
  h: number;
  label: string;
  subLabel?: string;
  entityName?: string;
  type?: EntityType;
  side?: Side;
  totalAmount?: number;
  allocatedOut?: number;
  received?: number;
  nodeId?: string;
}
interface Edge { x1: number; y1: number; x2: number; y2: number; }
interface AllocEdge extends Edge { amount: number; count: number; source: string; target: string; }

const RX = { root: 0.03, cat: 0.12, name: 0.22, card: 0.40 };
const PX = { root: 0.97, cat: 0.88, payer: 0.74 };

function buildLayout(nodes: FlowNode[], entities: Entity[], allocations: Allocation[], lang: Lang) {
  const entMap = new Map(entities.map((e) => [e.id, e]));
  const receivers = nodes.filter((n) => n.side === 'receiver');
  const payers = nodes.filter((n) => n.side === 'payer');

  const out = new Map<string, number>();
  const recv = new Map<string, number>();
  for (const a of allocations) {
    out.set(a.sourceNodeId, (out.get(a.sourceNodeId) || 0) + a.amount);
    recv.set(a.targetNodeId, (recv.get(a.targetNodeId) || 0) + a.amount);
  }
  const subOf = (n: FlowNode) => {
    if (!n.metadataJson) return undefined;
    try { return (JSON.parse(n.metadataJson) as { subLabel?: string }).subLabel; } catch { return undefined; }
  };

  const pos: Pos[] = [];
  const tree: Edge[] = [];
  const allocEdges: AllocEdge[] = [];

  const placeAll = (items: { kind: PosKind; cat?: EntityType; entity?: Entity; node?: FlowNode; w: number; h: number; y?: number; id: string; label: string }[]) => {
    let cursor = PAD_TOP;
    for (const it of items) {
      cursor += GAP / 2 + it.h / 2;
      it.y = cursor;
      cursor += it.h / 2 + GAP / 2;
    }
    return cursor; // total height
  };

  // ---- RECEIVERS ----
  const recvByType: Record<EntityType, Entity[]> = { bank: [], customer: [] };
  for (const e of entities) recvByType[e.type].push(e);
  const recvItems: { kind: PosKind; cat?: EntityType; entity?: Entity; node?: FlowNode; w: number; h: number; y?: number; xTier?: 'cat' | 'name' | 'card'; id: string; label: string }[] = [];
  for (const cat of ['bank', 'customer'] as EntityType[]) {
    const ents = recvByType[cat].slice().sort((a, b) => a.name.localeCompare(b.name)).filter((e) => receivers.some((r) => r.entityId === e.id));
    if (!ents.length) continue;
    recvItems.push({ kind: 'cat', cat, xTier: 'cat', w: 90, h: 30, id: 'rcat_' + cat, label: cat === 'bank' ? 'BANK' : 'CUSTOMER' });
    for (const e of ents) {
      const eNodes = receivers.filter((r) => r.entityId === e.id).sort((a, b) => a.createdAt - b.createdAt);
      const hasSub = eNodes.some((n) => subOf(n));
      if (hasSub) {
        recvItems.push({ kind: 'name', entity: e, xTier: 'name', w: NODE_W, h: NODE_H * 0.7, id: 'rname_' + e.id, label: e.name });
        for (const n of eNodes) recvItems.push({ kind: 'card', entity: e, node: n, xTier: 'card', w: NODE_W, h: NODE_H, id: 'rcard_' + n.id, label: subOf(n) || e.name });
      } else {
        for (const n of eNodes) recvItems.push({ kind: 'card', entity: e, node: n, xTier: 'name', w: NODE_W, h: NODE_H, id: 'rcard_' + n.id, label: e.name });
      }
    }
  }
  const recvH = placeAll(recvItems);

  // ---- PAYERS ----
  const payItems: { kind: PosKind; cat?: EntityType; entity?: Entity; node?: FlowNode; w: number; h: number; y?: number; id: string; label: string }[] = [];
  for (const cat of ['bank', 'customer'] as EntityType[]) {
    const ents = payers.filter((p) => entMap.get(p.entityId)?.type === cat).sort((a, b) => (entMap.get(a.entityId)?.name || '').localeCompare(entMap.get(b.entityId)?.name || '')).sort((a, b) => a.createdAt - b.createdAt);
    if (!ents.length) continue;
    payItems.push({ kind: 'cat', cat, w: 90, h: 30, id: 'pcat_' + cat, label: cat === 'bank' ? 'BANK' : 'CUSTOMER' });
    for (const n of ents) {
      const e = entMap.get(n.entityId);
      payItems.push({ kind: 'payer', entity: e, node: n, w: NODE_W, h: NODE_H, id: 'pnode_' + n.id, label: subOf(n) || e?.name || '?' });
    }
  }
  const payH = placeAll(payItems);

  const H = Math.max(recvH, payH) + 120;
  const shiftRecv = H / 2 - recvH / 2;
  const shiftPay = H / 2 - payH / 2;

  for (const it of recvItems) {
    const cy = (it.y || 0) + shiftRecv;
    const kind = it.kind;
    const id = it.id;
    const x = it.xTier === 'cat' ? RX.cat * VW : it.xTier === 'name' ? RX.name * VW : it.xTier === 'card' ? RX.card * VW : RX.card * VW;
    if (kind === 'cat') pos.push({ id, kind, x, y: cy, w: it.w, h: it.h, label: it.label });
    else if (kind === 'name') pos.push({ id, kind, x, y: cy, w: it.w, h: it.h, label: it.label, entityName: it.entity?.name, type: it.entity?.type });
    else pos.push({ id, kind, x, y: cy, w: it.w, h: it.h, nodeId: it.node?.id, label: it.label, subLabel: subOf(it.node!), entityName: it.entity?.name, type: it.entity?.type, side: 'receiver', totalAmount: it.node?.totalAmount, allocatedOut: it.node ? out.get(it.node.id) || 0 : 0, received: it.node ? recv.get(it.node.id) || 0 : 0 });
  }
  for (const it of payItems) {
    const cy = (it.y || 0) + shiftPay;
    const kind = it.kind;
    const id = it.id;
    const x = kind === 'cat' ? PX.cat * VW : PX.payer * VW;
    if (kind === 'cat') pos.push({ id, kind, x, y: cy, w: it.w, h: it.h, label: it.label });
    else pos.push({ id, kind, x, y: cy, w: it.w, h: it.h, nodeId: it.node?.id, label: it.label, subLabel: subOf(it.node!), entityName: it.entity?.name, type: it.entity?.type, side: 'payer', totalAmount: it.node?.totalAmount, allocatedOut: it.node ? out.get(it.node.id) || 0 : 0, received: it.node ? recv.get(it.node.id) || 0 : 0 });
  }

  // root buttons (replace the old diamond)
  const recvRootY = (recvItems.find((i) => i.kind === 'cat')?.y || 0) + shiftRecv;
  const payRootY = (payItems.find((i) => i.kind === 'cat')?.y || 0) + shiftPay;
  pos.push({ id: 'rroot', kind: 'root', x: RX.root * VW, y: recvRootY, w: 120, h: 46, label: t('receivers', lang) });
  pos.push({ id: 'proot', kind: 'root', x: PX.root * VW, y: payRootY, w: 120, h: 46, label: t('payers', lang) });

  const byId = (id: string) => pos.find((p) => p.id === id);
  // tree edges
  for (const cat of ['bank', 'customer'] as EntityType[]) {
    const rcat = byId('rcat_' + cat); const pcat = byId('pcat_' + cat);
    const rroot = byId('rroot'); const proot = byId('proot');
    if (rcat && rroot) tree.push({ x1: rroot.x + rroot.w / 2, y1: rroot.y, x2: rcat.x, y2: rcat.y });
    if (pcat && proot) tree.push({ x1: proot.x - proot.w / 2, y1: proot.y, x2: pcat.x, y2: pcat.y });
  }
  for (const p of pos) {
    if (p.kind === 'name' && p.entityName) {
      const cat = byId('rcat_' + (p.type === 'bank' ? 'bank' : 'customer'));
      if (cat) tree.push({ x1: cat.x, y1: cat.y, x2: p.x, y2: p.y });
    }
    if (p.kind === 'card' && p.entityName) {
      const nameNode = pos.find((q) => q.kind === 'name' && q.entityName === p.entityName);
      const cat = byId('rcat_' + (p.type === 'bank' ? 'bank' : 'customer'));
      if (nameNode) tree.push({ x1: nameNode.x, y1: nameNode.y, x2: p.x, y2: p.y });
      else if (cat) tree.push({ x1: cat.x, y1: cat.y, x2: p.x, y2: p.y });
    }
    if (p.kind === 'payer' && p.entityName) {
      const cat = byId('pcat_' + (p.type === 'bank' ? 'bank' : 'customer'));
      if (cat) tree.push({ x1: cat.x, y1: cat.y, x2: p.x, y2: p.y });
    }
  }

  // merged allocation edges
  const merged = new Map<string, { amount: number; count: number; source: string; target: string }>();
  for (const a of allocations) {
    const key = a.sourceNodeId + '>' + a.targetNodeId;
    const cur = merged.get(key) || { amount: 0, count: 0, source: a.sourceNodeId, target: a.targetNodeId };
    cur.amount += a.amount; cur.count += 1; merged.set(key, cur);
  }
  for (const m of merged.values()) {
    const s = byId('pnode_' + m.source); const tg = byId('rcard_' + m.target);
    if (s && tg) allocEdges.push({ x1: s.x - s.w / 2, y1: s.y, x2: tg.x + tg.w / 2, y2: tg.y, amount: m.amount, count: m.count, source: m.source, target: m.target });
  }

  return { pos, tree, allocEdges, H };
}

interface Props {
  onNodeClick: (node: FlowNode) => void;
  onNodeEdit: (node: FlowNode) => void;
  onNodeHoldDelete: (node: FlowNode) => void;
  onAllocEdgeDelete: (sourceNodeId: string, targetNodeId: string) => void;
  onAddPayer: () => void;
  onAddReceiver: () => void;
}

export function Canvas({ onNodeClick, onNodeEdit, onNodeHoldDelete, onAllocEdgeDelete, onAddPayer, onAddReceiver }: Props) {
  const nodes = useStore((s) => s.nodes);
  const entities = useStore((s) => s.entities);
  const allocations = useStore((s) => s.allocations);
  const lang = useStore((s) => s.lang);
  const currency = useStore((s) => s.currency);
  const zoom = useStore((s) => s.zoom);
  const panX = useStore((s) => s.panX);
  const panY = useStore((s) => s.panY);
  const setView = useStore((s) => s.setView);

  const svgRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  const holdTimer = useRef<number | null>(null);
  const [holdId, setHoldId] = useState<string | null>(null);

  const { pos, tree, allocEdges, H } = useMemo(() => buildLayout(nodes, entities, allocations, lang), [nodes, entities, allocations, lang]);

  const onWheel = useCallback((e: React.WheelEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const nz = Math.min(3, Math.max(0.2, zoom * (1 - e.deltaY * 0.001)));
    const wx = (px - panX) / zoom;
    const wy = (py - panY) / zoom;
    setView(nz, px - wx * nz, py - wy * nz);
  }, [zoom, panX, panY, setView]);

  const onPointerDownBg = (e: React.PointerEvent) => {
    drag.current = { x: e.clientX, y: e.clientY, px: panX, py: panY };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMoveBg = (e: React.PointerEvent) => {
    if (!drag.current) return;
    setView(zoom, drag.current.px + (e.clientX - drag.current.x), drag.current.py + (e.clientY - drag.current.y));
  };
  const onPointerUpBg = () => { drag.current = null; };

  const pinch = useRef<{ dist: number; zoom: number } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      pinch.current = { dist, zoom };
      drag.current = null;
    }
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinch.current) {
      e.preventDefault();
      const [a, b] = [e.touches[0], e.touches[1]];
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const cx = (a.clientX + b.clientX) / 2 - rect.left;
      const cy = (a.clientY + b.clientY) / 2 - rect.top;
      const nz = Math.min(3, Math.max(0.2, pinch.current.zoom * (dist / pinch.current.dist)));
      const wx = (cx - panX) / pinch.current.zoom;
      const wy = (cy - panY) / pinch.current.zoom;
      setView(nz, cx - wx * nz, cy - wy * nz);
    }
  };
  const onTouchEnd = (e: React.TouchEvent) => { if (e.touches.length < 2) pinch.current = null; };

  const startHold = (node: FlowNode) => {
    setHoldId(node.id);
    holdTimer.current = window.setTimeout(() => {
      setHoldId(null);
      onNodeHoldDelete(node);
    }, 2000);
  };
  const cancelHold = () => {
    if (holdTimer.current) { clearTimeout(holdTimer.current); holdTimer.current = null; }
    setHoldId(null);
  };

  const nodeById = (id?: string) => nodes.find((n) => n.id === id);
  const fmt = (v: number) => formatMoney(v, currency, lang);

  const zoomBy = (f: number) => setView(Math.min(3, Math.max(0.2, zoom * f)), 0, 0);
  const center = useCallback(() => {
    const el = svgRef.current;
    if (!el) return;
    const z = zoom;
    setView(z, (el.clientWidth - VW * z) / 2, (el.clientHeight - H * z) / 2);
  }, [zoom, H, setView]);
  const didCenter = useRef(false);
  useEffect(() => {
    if (!didCenter.current && H > 200) { center(); didCenter.current = true; }
  }, [H, center]);

  return (
    <div
      ref={svgRef}
      className="canvas-bg"
      onWheel={onWheel}
      onPointerDown={onPointerDownBg}
      onPointerMove={onPointerMoveBg}
      onPointerUp={onPointerUpBg}
      onPointerLeave={onPointerUpBg}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
        <div className="side-header left" onPointerDown={(e) => e.stopPropagation()}>
          <span className="sh-title">{t('receivers', lang)}</span>
          <button className="sh-plus" title={t('addReceiver', lang)} onClick={(e) => { e.stopPropagation(); onAddReceiver(); }}>+</button>
        </div>
        <div className="side-header right" onPointerDown={(e) => e.stopPropagation()}>
          <span className="sh-title">{t('payers', lang)}</span>
          <button className="sh-plus" title={t('addPayer', lang)} onClick={(e) => { e.stopPropagation(); onAddPayer(); }}>+</button>
        </div>
        <div className="canvas-world" style={{ width: VW, height: H, transform: `translate(${panX}px, ${panY}px) scale(${zoom})`, transformOrigin: '0 0', userSelect: 'none' }}>
          <svg className="edges" width={VW} height={H}>
          {tree.map((e, i) => (
            <path key={'t' + i} className="tree-edge" d={`M ${e.x1} ${e.y1} C ${(e.x1 + e.x2) / 2} ${e.y1}, ${(e.x1 + e.x2) / 2} ${e.y2}, ${e.x2} ${e.y2}`} />
          ))}
          {allocEdges.map((e, i) => {
            const mx = (e.x1 + e.x2) / 2;
            const d = `M ${e.x1} ${e.y1} C ${mx} ${e.y1}, ${mx} ${e.y2}, ${e.x2} ${e.y2}`;
            return (
              <g key={'a' + i} className="alloc-group" onClick={() => onAllocEdgeDelete(e.source, e.target)}>
                <path className="alloc-edge-hit" d={d} />
                <path className="alloc-edge" d={d} />
                <foreignObject x={mx - 38} y={(e.y1 + e.y2) / 2 - 12} width={76} height={24}>
                  <div className="alloc-pill" dir="ltr">{fmt(e.amount)}{e.count > 1 ? ` ×${e.count}` : ''}</div>
                </foreignObject>
              </g>
            );
          })}
        </svg>

        {pos.map((p) => {
          if (p.kind === 'root') {
            return (
              <button
                key={p.id}
                className="root-btn"
                style={{ left: p.x - p.w / 2, top: p.y - p.h / 2, width: p.w, height: p.h }}
                onClick={(e) => { e.stopPropagation(); if (p.id === 'rroot') onAddReceiver(); else onAddPayer(); }}
              >
                {p.label}
              </button>
            );
          }
          if (p.kind === 'cat') {
            return <div key={p.id} className="cat-label" style={{ left: p.x - p.w / 2, top: p.y - p.h / 2, width: p.w, height: p.h }}>{p.label}</div>;
          }
          if (p.kind === 'name') {
            return <div key={p.id} className="name-node" style={{ left: p.x - p.w / 2, top: p.y - p.h / 2, width: p.w, height: p.h }}>{p.entityName}</div>;
          }
          const node = nodeById(p.nodeId);
          if (!node) return null;
          const isPayer = p.side === 'payer';
          const used = isPayer ? p.allocatedOut || 0 : p.received || 0;
          const total = p.totalAmount || 0;
          const status = total > 0 && used >= total ? 'done' : used <= 0 ? 'none' : 'partial';
          const outstanding = total - used;
          return (
            <div
              key={p.id}
              className={`flow-node ${isPayer ? 'payer' : 'receiver'} ${status === 'done' ? 'alloc-done' : status === 'none' ? 'alloc-none' : 'alloc-partial'} ${holdId === p.nodeId ? 'holding' : ''}`}
              style={{ left: p.x - p.w / 2, top: p.y - p.h / 2, width: p.w, height: p.h }}
              onPointerDown={(e) => { e.stopPropagation(); startHold(node); }}
              onPointerUp={(e) => { e.stopPropagation(); cancelHold(); }}
              onPointerLeave={cancelHold}
              onClick={(e) => { e.stopPropagation(); if (holdId !== p.nodeId) onNodeClick(node); }}
              title={t('holdToDelete', lang)}
            >
              <div className="node-label">{p.label}</div>
              {p.subLabel && <div className="node-sub">{p.subLabel}</div>}
              {outstanding > 0 && <div className="node-meta">{fmt(outstanding)}</div>}
            </div>
          );
        })}
      </div>
      <div className="zoombar">
        <button className="btn icon" onClick={() => zoomBy(1.2)}>+</button>
        <button className="btn icon" onClick={() => zoomBy(1 / 1.2)}>−</button>
        <button className="btn icon" onClick={center}>{t('resetView', lang)}</button>
      </div>
    </div>
  );
}
